interface BalancesByKind {
  commissions: number;
  donations: number;
  loans: number;
  deposits: number;
  supports: number;
  scholarships: number;
  suppliersHok: number;
  suppliersNotHok: number;
  expenses: number;
  other: number;
  total: number;
}

interface SupportRequestsBalances {
  canRequestSupport: boolean;
  pendingExecutionSum: number;
  pendingExecutionSumSingleMonth?: number;
  pendingExecutionSumMultiMonth?: number;
  frozenRequestsSum?: number;
  /** HAT-1396: the fund's fee per support payment; a new request costs its amount plus this. */
  supportRowFee?: number;
  /** HAT-1396: support fees of the pending payments; inside pendingExecutionSum, not inside the single/multi sums. */
  pendingSupportFeesSum?: number;
  /**
   * SPR-10304: standing-support payments due in the coming days (after today), one per due date,
   * sorted. The server no longer deducts them from the available balance; the new-request form
   * shows them as a notice instead.
   */
  upcomingStandingSupports?: UpcomingStandingSupport[];
}

export interface UpcomingStandingSupport {
  /** ISO date-time of the due date (date part is what matters). */
  date: string;
  /** Sum of the approved amounts due that day, without the support-row fees. */
  sum: number;
  /** How many payments make up the sum. */
  count: number;
}

class Balance {
  fundId!: number;
  fundName!: string;
  balancesByKind!: BalancesByKind;
  supportRequestsBalances!: SupportRequestsBalances;
  overdraftAmount!: number;
  /**
   * Amount frozen against Nedarim devices held by the fund. The server sends it on every
   * balance response; only "תמונת מצב" reads it (see the note on
   * calculateAvailableBalance).
   */
  nedarimDevicesFreezeAmount!: number;
  currency!: string;
  /** HAT-1448: the first date the transactions board has a running balance for; absent where there is none. */
  runningBalanceFrom?: string | null;

  /**
   * Note: To use this method, you must convert the object from the server:
   * const balance = Object.assign(new Balance(), balanceData);
   *
   * KNOWN GAP, deliberately left as-is: this formula does not subtract
   * nedarimDevicesFreezeAmount, while the server (FundBalances.CalculateAvailableBalance)
   * and the management-side helper (FundManagementDetailsDtoIn.calculateAvailableBalance)
   * both do. On a fund holding Nedarim devices a coordinator is therefore shown more
   * available balance here than the fund actually has, on the balance bar and in the
   * new-support-request flow.
   *
   * Correcting it changes a number every coordinator sees in production, so it is a
   * deliberate decision and not a side effect of shipping a flag-gated screen.
   * "תמונת מצב" - which is behind the `dashboard` flag - computes the server's formula
   * itself instead of calling this method, so the fix is visible only there until the
   * decision is made. When it is made, delete that local computation and subtract the
   * devices freeze here.
   */
  calculateAvailableBalance(): number {
    const total = this.balancesByKind?.total ?? 0;
    const pending = this.supportRequestsBalances?.pendingExecutionSum ?? 0;
    const overdraft = this.overdraftAmount ?? 0;
    const frozen = this.supportRequestsBalances?.frozenRequestsSum ?? 0;
    return total - pending - overdraft - frozen;
  }
}

export default Balance;
